import { isEqual } from "lodash";
import { useSnackbar } from "notistack";
import { useState } from "react";
import {
  GetResourceTypesQuery,
  Kind,
  PipelineType,
  ResourceConfiguration,
} from "../../graphql/generated";
import { BPExtension } from "../../utils/classes/extension";
import { BPProcessor } from "../../utils/classes/processor";
import { UpdateStatus } from "../../types/resources";
import { BPResourceConfiguration } from "../../utils/classes";
import { applyResources } from "../../utils/rest/apply-resources";
import { FormValues } from "../ResourceConfigForm";
import { AllItemsView } from "./AllItemsView";
import { CreateConfigureView } from "./CreateConfigureView";
import { EditResourceView } from "./EditResourceView";
import { SelectView } from "./SelectView";
import { ChooseView } from "./ChooseView";
import { trimVersion } from "../../utils/version-helpers";
import { DialogResource } from "../ResourceDialog";
import { ReusableProcessors } from "../ProcessorsDialog/ProcessorDialog";
import { ApolloError } from "@apollo/client";
import { isFunction } from "lodash";

enum Page {
  MAIN,
  CREATE_RESOURCE_SELECT,
  CREATE_RESOURCE_CONFIGURE,
  EDIT_RESOURCE,
  CHOOSE_VIEW,
}

interface ResourceConfigurationEditorProps {
  // The initial list of processors or extensions to edit
  initItems: ResourceConfiguration[];
  // The stateful value of the current resource configurations
  items: ResourceConfiguration[];
  // Called to change the current state of resourceConfigurations
  onItemsChange: (newItems: ResourceConfiguration[]) => void;

  readOnly?: boolean;

  // The types of telemetry that are available for the pipeline, used to determine
  // which resource types can be used.
  telemetryTypes: PipelineType[];

  kind: Kind.Processor | Kind.Extension;

  reusableResources?: ReusableProcessors; // or extensions

  fetchReusableResourcesError?: ApolloError;

  refetchReusableResources?: () => void;

  refetchConfiguration: () => void;

  closeDialog: () => void;

  // updateInlineItems will be called when the user saves the inline items.
  // For processors it should handle saving the inline processors on that
  // item, and for extensions it should handle saving the inline extensions
  // on the configuration.  It should return true if saving was successful
  // and false if saving failed. There is no handling in the component, it is
  // up to the provider of the function to handle the saving and error.
  updateInlineItems: (items: ResourceConfiguration[]) => Promise<boolean>;
}

export type ResourceType = GetResourceTypesQuery["resourceTypes"][0];

export const ResourceConfigurationEditor: React.FC<
  ResourceConfigurationEditorProps
> = ({
  items,
  initItems: itemsProp,
  onItemsChange,
  readOnly,
  telemetryTypes,
  kind,
  reusableResources,
  fetchReusableResourcesError,
  refetchReusableResources,
  updateInlineItems,
  refetchConfiguration,
  closeDialog,
}) => {
  const [view, setView] = useState<Page>(Page.MAIN);
  const [newResourceType, setNewResourceType] = useState<ResourceType | null>(
    null,
  );
  const [editingItemIndex, setEditingItemIndex] = useState<number>(-1);
  const [applyQueue, setApplyQueue] = useState<(BPProcessor | BPExtension)[]>(
    [],
  );

  const [matchingResources, setMatchingResources] =
    useState<ReusableProcessors>();

  const { enqueueSnackbar } = useSnackbar();

  /* -------------------------------- Functions ------------------------------- */

  function handleReturnToAll() {
    setView(Page.MAIN);
    setNewResourceType(null);
  }

  function handleReturnToSelectView() {
    setView(Page.CREATE_RESOURCE_SELECT);
    setNewResourceType(null);
    refetchReusableResources && refetchReusableResources();
  }

  // handleSelectNewProcessorType is called when a user selects a processor type
  // in the CreateProcessorSelect view.
  function handleSelectNewResourceType(type: ResourceType) {
    if (fetchReusableResourcesError) {
      console.error(fetchReusableResourcesError);
      enqueueSnackbar("Error retrieving reusable resources", {
        variant: "error",
      });
      refetchReusableResources && refetchReusableResources();
    }
    const matchingResources = reusableResources?.filter((p) => {
      return trimVersion(p.spec.type) === type.metadata.name;
    });
    setNewResourceType(type);
    if (matchingResources && matchingResources?.length > 0) {
      setMatchingResources(matchingResources);
      setView(Page.CHOOSE_VIEW);
    } else {
      setView(Page.CREATE_RESOURCE_CONFIGURE);
    }
  }

  function handleCreateNewResource() {
    setView(Page.CREATE_RESOURCE_CONFIGURE);
  }

  function handleEnterSelectView() {
    setView(Page.CREATE_RESOURCE_SELECT);
    refetchReusableResources && refetchReusableResources();
  }

  // handleAddProcessor adds a new processor to the list of processors
  async function handleAddItem(formValues: FormValues) {
    const itemConfig = new BPResourceConfiguration();
    itemConfig.setParamsFromMap(formValues);
    itemConfig.type = newResourceType!.metadata.name;

    const newItems = [...items, itemConfig];
    onItemsChange(newItems);
    handleReturnToAll();
  }

  // handleSaveExistingInlineResource saves changes to an existing resourceConfiguration in the list
  function handleSaveExistingInlineResource(formValues: FormValues) {
    const itemConfig = new BPResourceConfiguration(items[editingItemIndex]);
    itemConfig.setParamsFromMap(formValues);

    const newItems = [...items];
    newItems[editingItemIndex] = itemConfig;
    onItemsChange(newItems);

    handleReturnToAll();
  }

  // handleSaveExistingPersistentResource adds a processor to the apply queue
  function handleSaveExistingPersistentResource(
    resource: BPProcessor | BPExtension,
  ) {
    const foundIndex = applyQueue.findIndex(
      (p) => p.name() === resource.name(),
    );
    if (foundIndex !== -1) {
      const newApplyQueue = [...applyQueue];
      newApplyQueue[foundIndex] = resource;
      setApplyQueue(newApplyQueue);
    } else {
      setApplyQueue([...applyQueue, resource]);
    }

    handleReturnToAll();
  }

  function handleSaveReusableResourceChoice(resource: DialogResource) {
    const processorConfig = new BPResourceConfiguration();
    processorConfig.name = resource.metadata.name;
    processorConfig.type = trimVersion(resource.spec.type);
    processorConfig.parameters = resource.spec.parameters;

    const processors = [...items, processorConfig];
    onItemsChange(processors);

    handleReturnToAll();
  }

  async function onAddToLibrary(values: { [key: string]: any }, name: string) {
    const reusableProcessor = new BPProcessor({
      metadata: {
        name: name,
        id: "",
        version: 0,
        displayName: values.displayName,
      },
      spec: {
        parameters: [],
        type: items[editingItemIndex].type!,
        disabled: items[editingItemIndex].disabled,
      },
    });

    reusableProcessor.setParamsFromMap(values);

    try {
      await applyResources([reusableProcessor]);
      enqueueSnackbar(`Successfully added ${kind} to Library!`, {
        variant: "success",
        autoHideDuration: 3000,
      });
    } catch (err) {
      enqueueSnackbar(`Failed to add ${kind} to Library.`, {
        variant: "error",
        autoHideDuration: 5000,
      });
      console.error(err);
      return;
    }

    const updatedProcessor = new BPResourceConfiguration({
      name: reusableProcessor.metadata.name,
      disabled: reusableProcessor.spec.disabled,
      displayName: reusableProcessor.metadata.displayName,
    });

    // find the old inline item and replace it with this one
    const newItems = items.map((i, idx) => {
      if (idx === editingItemIndex) {
        return updatedProcessor;
      } else {
        return i;
      }
    });

    onItemsChange(newItems);

    updateInlineItems(newItems);

    isFunction(refetchReusableResources)
      ? refetchReusableResources()
      : console.error("No function provided for refetchReusableResources");
  }

  // handleRemoveItem removes a processor from the list of processors
  async function handleRemoveItem(index: number) {
    const newItems = [...items];
    newItems.splice(index, 1);
    onItemsChange(newItems);

    handleReturnToAll();
  }

  // handleEditItemClick sets the editing index and switches to the edit page
  function handleEditItemClick(index: number) {
    setEditingItemIndex(index);
    setView(Page.EDIT_RESOURCE);
  }

  // handleSave saves the processors to the backend and closes the dialog.
  async function handleSave() {
    const inlineChange = !isEqual(itemsProp, items);
    const resourceChange = applyQueue.length > 0;
    var shouldCloseDialog = true;

    if (!inlineChange && !resourceChange) {
      closeDialog();
    }

    if (resourceChange) {
      const { updates } = await applyResources(applyQueue);
      if (updates.some((u) => u.status === UpdateStatus.INVALID)) {
        enqueueSnackbar("Failed to save resources", {
          variant: "error",
          key: "save-resources-error",
        });
        shouldCloseDialog = false;
      }
    }

    if (inlineChange) {
      shouldCloseDialog = await updateInlineItems(items);
    }

    if (shouldCloseDialog) {
      refetchConfiguration();
      closeDialog();
      enqueueSnackbar(`Saved ${kind}s! 🎉`, { variant: "success" });
    }
  }

  let current: JSX.Element;
  switch (view) {
    case Page.MAIN:
      current = (
        <AllItemsView
          resourceKind={kind}
          items={items}
          onAddItem={handleEnterSelectView}
          onEditItem={handleEditItemClick}
          onSave={handleSave}
          onItemsChange={onItemsChange}
          readOnly={Boolean(readOnly)}
        />
      );
      break;
    case Page.CREATE_RESOURCE_SELECT:
      current = (
        <SelectView
          resourceKind={kind}
          telemetryTypes={telemetryTypes}
          onBack={() => setView(Page.MAIN)}
          onSelect={handleSelectNewResourceType}
        />
      );
      break;
    case Page.CREATE_RESOURCE_CONFIGURE:
      current = (
        <CreateConfigureView
          resourceKind={kind}
          resourceType={newResourceType!}
          onBack={handleReturnToSelectView}
          onSave={handleAddItem}
          onClose={closeDialog}
        />
      );
      break;
    case Page.EDIT_RESOURCE:
      current = (
        <EditResourceView
          resourceKind={kind}
          items={items}
          editingIndex={editingItemIndex}
          applyQueue={applyQueue}
          onEditInlineSave={handleSaveExistingInlineResource}
          onEditPersistentResourceSave={handleSaveExistingPersistentResource}
          onBack={handleReturnToAll}
          onRemove={handleRemoveItem}
          readOnly={readOnly}
          libraryResources={reusableResources}
          onAddToLibrary={onAddToLibrary}
        />
      );
      break;
    case Page.CHOOSE_VIEW:
      current = (
        <ChooseView
          resourceKind={kind}
          onBack={handleReturnToSelectView}
          onCreate={handleCreateNewResource}
          reusableResources={matchingResources!}
          selected={newResourceType!}
          handleSaveExisting={handleSaveReusableResourceChoice}
        />
      );
  }

  return current;
};
